import { useCallback, useEffect, useRef } from 'react';
import { useFormikContext, FieldArray } from 'formik';
import { useTranslation } from 'react-i18next';
import { getRouteDistanceWithWaypoints } from 'helpers/routeDistance';

import FormikCheckboxField from 'components/InputsField/formikInputs/FormikCheckboxField';
import CityAutoCompleteSelect from 'components/InputsField/formikInputs/CityAutoCompleteSelect';
import FormRow, { ALIGN } from 'components/common/Layout/FormRow';
import TextButton from 'components/common/TextButton';
import IconButton, { ICON } from 'components/common/IconButton';
import Title from 'components/common/Layout/Title';
import styles from './styles.module.scss';

const MAX_ADDITIONAL_ROUTES = 5;

export default function FreightsRoutes() {
  const { values, setFieldValue } = useFormikContext();
  const { t } = useTranslation();
  const isCalculating = useRef(false);

  // Calculate route distance when addresses change
  useEffect(() => {
    const calculateDistance = async () => {
      const loadingAddress = values.loading_address?.value || values.loading_address;
      const unloadingAddress = values.unloading_address?.value || values.unloading_address;

      if (!loadingAddress?.location || !unloadingAddress?.location) {
        return;
      }

      if (isCalculating.current) return;
      isCalculating.current = true;

      try {
        const additionalLoading = (values.additional_loading_addresses || [])
          .map(addr => addr?.value || addr)
          .filter(Boolean);
        const additionalUnloading = (values.additional_unloading_addresses || [])
          .map(addr => addr?.value || addr)
          .filter(Boolean);

        const waypoints = [...additionalLoading, ...additionalUnloading];
        const distance = await getRouteDistanceWithWaypoints(loadingAddress, unloadingAddress, waypoints);

        if (distance !== null) {
          setFieldValue('route_distance_meters', distance);
        }
      } catch (error) {
        console.error('Failed to calculate route distance:', error);
      } finally {
        isCalculating.current = false;
      }
    };

    calculateDistance();
  }, [
    values.loading_address,
    values.unloading_address,
    values.additional_loading_addresses,
    values.additional_unloading_addresses,
    setFieldValue
  ]);

  const getTranslation = useCallback(
    (str) => {
      return t('cargoFields.itinerary_section.' + str);
    },
    [t],
  );

  return (
    <>
      <Title withMargin>{getTranslation('uploading_heading')}</Title>
      <FormRow align={ALIGN.top}>
        <CityAutoCompleteSelect
          noOptionsMessage={getTranslation('no_option')}
          name="loading_address"
          required
          label={getTranslation('uploading')}
          note={getTranslation('city_note')}
          useMapboxSearch
          withMapPicker
        />
      </FormRow>

      {
        <FieldArray
          name="additional_loading_addresses"
          render={(arrayHelpers) => (
            <>
              {values.additional_loading_addresses &&
                values.additional_loading_addresses.length > 0 &&
                values.additional_loading_addresses.map((address, index) => (
                  <FormRow key={address?.value || address?.id || `loading-${index}`} withMargin>
                    <div className={styles.additional_loading_routes_container}>
                      <CityAutoCompleteSelect
                        noOptionsMessage={getTranslation('no_option')}
                        name={`additional_loading_addresses[${index}]`}
                        required
                        label={getTranslation('uploading')}
                        useMapboxSearch
                        withMapPicker
                      />
                      <IconButton
                        type="button"
                        icon={ICON.delete}
                        onClick={() => arrayHelpers.remove(index)}
                        transparent
                      />
                    </div>
                  </FormRow>
                ))}
              {(!values.additional_loading_addresses ||
                values.additional_loading_addresses.length < MAX_ADDITIONAL_ROUTES) && (
                <FormRow>
                  <TextButton onClick={() => arrayHelpers.push()} className={styles.button__add}>
                    + {getTranslation('add_waypoint_button')}
                  </TextButton>
                </FormRow>
              )}
            </>
          )}
        />
      }
      <Title withMargin>{getTranslation('unloading_heading')}</Title>
      <FormRow align={ALIGN.top}>
        <CityAutoCompleteSelect
          noOptionsMessage={getTranslation('no_option')}
          name="unloading_address"
          required
          label={getTranslation('unloading')}
          note={getTranslation('city_note')}
          useMapboxSearch
          withMapPicker
        />
      </FormRow>
      {
        <FieldArray
          name="additional_unloading_addresses"
          render={(arrayHelpers) => (
            <>
              {values.additional_unloading_addresses &&
                values.additional_unloading_addresses.length > 0 &&
                values.additional_unloading_addresses.map((friend, index) => (
                  <FormRow key={friend?.value || friend?.id || `unloading-${index}`} withMargin>
                    <div className={styles.additional_loading_routes_container}>
                      <CityAutoCompleteSelect
                        noOptionsMessage={getTranslation('no_option')}
                        name={`additional_unloading_addresses[${index}]`}
                        required
                        label={getTranslation('unloading')}
                        useMapboxSearch
                        withMapPicker
                      />
                      <IconButton
                        type="button"
                        icon={ICON.delete}
                        onClick={() => arrayHelpers.remove(index)}
                        transparent
                      />
                    </div>
                  </FormRow>
                ))}
              {(!values.additional_unloading_addresses ||
                values.additional_unloading_addresses.length < MAX_ADDITIONAL_ROUTES) && (
                <FormRow>
                  <TextButton onClick={() => arrayHelpers.push()} className={styles.button__add}>
                    + {getTranslation('add_waypoint_button')}
                  </TextButton>
                </FormRow>
              )}
            </>
          )}
        />
      }
      <FormRow withMargin>
        <FormikCheckboxField
          label={getTranslation('round_trip_box')}
          id="round_trip"
          name="round_trip"
          labelClasses={'add__sub-info'}
        />
      </FormRow>
    </>
  );
}
